'use client';

import { Fragment, useState, createRef, useEffect } from 'react';
import Link from 'next/link';
import useSWR from 'swr';

import { fetchJsonWithQuery } from 'lib/fetch-json';
import Spinner from 'src/components/Spinner';
import CategoryPosts from './CategoryPosts';
import { blogCategoryUrl } from 'src/services/route-utils';

type Props = {
    initialData: {
        data: {
            category: any;
            posts: any[];
        }[];
        total: number;
    };
};

export default function InfiniteCategoriesPosts({ initialData }: Props) {
    const elementRef = createRef<HTMLDivElement>();
    const [page, setPage] = useState(1);
    const [categoryPosts, setCategoryPosts] = useState(initialData.data);
    const { data, isLoading } = useSWR<any>(
        page === 1 ? null : [`/api/categories-posts?page=${page}`],
        fetchJsonWithQuery,
    );

    const onScroll = () => {
        const element = elementRef.current;
        const loadMoreTop = (element?.offsetTop || 0) + (element?.clientHeight || 0) - 1.5 * window.innerHeight;
        if ((document.scrollingElement?.scrollTop || 0) >= loadMoreTop) {
            document.removeEventListener('scroll', onScroll);
            setPage((page) => page + 1);
        }
    };

    useEffect(() => {
        document.addEventListener('scroll', onScroll);

        return () => {
            document.removeEventListener('scroll', onScroll);
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (page > 1) {
            const hasMore = categoryPosts.length < initialData.total;
            if (hasMore) {
                document.addEventListener('scroll', onScroll);
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categoryPosts]);

    useEffect(() => {
        if (data) {
            setCategoryPosts((categoryPosts) => [...categoryPosts, ...data.data]);
        }
    }, [data]);

    return (
        <>
            <div ref={elementRef}>
                {categoryPosts.map((item) =>
                    item.posts.length ? (
                        <Fragment key={item.category.slug}>
                            <div className="container mx-auto px-4">
                                <h2 className="text-3xl">
                                    <Link
                                        href={blogCategoryUrl(item.category)}
                                        className="decoration-1 underline-offset-4 hover:underline"
                                        prefetch={false}>
                                        {item.category.name}
                                    </Link>
                                </h2>
                            </div>
                            <CategoryPosts category={item.category} posts={item.posts} />
                        </Fragment>
                    ) : null,
                )}
            </div>
            {isLoading && (
                <div className="flex h-80 items-center justify-center">
                    <Spinner />
                </div>
            )}
        </>
    );
}
